import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DetailHeader, FaixaKpis, PilulaStatus, type Kpi } from "@/components/padroes";
import { LancarBeneficioModal } from "@/components/beneficios/LancarBeneficioModal";
import { EditarBeneficiosModal } from "@/components/beneficios/EditarBeneficiosModal";
import { RecalcularBeneficiosModal } from "@/components/beneficios/RecalcularBeneficiosModal";
import { OrdenarColunasModal, type ColunaOrdenavel } from "@/components/beneficios/OrdenarColunasModal";
import { ImportarFlashModal } from "@/components/beneficios/ImportarFlashModal";
import { ExportarMenu } from "@/components/beneficios/ExportarMenu";
import { Paginacao } from "@/components/Paginacao";
import { useDebounce, usePaginado } from "@/hooks/usePaginado";
import { api } from "@/lib/api";
import { formatCPF, formatCents, formatDateTime, formatMesRef } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  formatDataCivil,
  soDigitos,
  type BeneficioCatalogo,
  type LinhaFolhaBeneficio,
  type PaginaFolhaBeneficio,
} from "@/data/beneficios";
import type { User } from "@/data/mock";
import type { LancamentosErp } from "@/data/erp";
import { ErpLancamentosPainel } from "@/components/erp/ErpLancamentosPainel";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Pencil, Plus, RefreshCw, Search, Send, Settings, Trash2, Upload } from "lucide-react";

/** Badge PJ/CLT nas cores do gráfico (PJ azul, CLT laranja). */
const BadgeVinculo = ({ vinculo }: { vinculo: string }) => (
  <span
    className={cn(
      "rounded px-1.5 py-px text-[10px] font-bold uppercase",
      vinculo === "clt"
        ? "bg-[#eb6834]/[0.12] text-[#b9481c] dark:text-[#f08a5d]"
        : "bg-[#2a78d6]/[0.12] text-[#1f5fae] dark:text-[#6aa5ec]",
    )}
  >
    {vinculo === "clt" ? "CLT" : "PJ"}
  </span>
);


const RHBeneficioDetalhe = () => {
  const { id = "" } = useParams();
  const nav = useNavigate();

  const [catalogo, setCatalogo] = useState<BeneficioCatalogo[]>([]);
  const [usuarios, setUsuarios] = useState<User[]>([]);
  const [busca, setBusca] = useState("");
  const buscaAtrasada = useDebounce(busca.trim());

  // A tabela vem paginada (e filtrada pela busca) do servidor; `resumo` traz os
  // totais da folha inteira: colunas, rodapé, aviso de cadastro e exportação.
  const {
    items: linhas,
    total: totalLinhas,
    page,
    pageSize,
    carregando,
    setPage,
    recarregar: load,
    resposta,
  } = usePaginado<LinhaFolhaBeneficio, PaginaFolhaBeneficio>(
    `/beneficios/folhas/${id}`,
    { q: buscaAtrasada },
    { pageSize: 50 },
  );
  const folha = resposta?.folha ?? null;
  const resumo = resposta?.resumo ?? null;

  const [lancarOpen, setLancarOpen] = useState(false);
  const [editando, setEditando] = useState<LinhaFolhaBeneficio | null>(null);
  const [removendo, setRemovendo] = useState<LinhaFolhaBeneficio | null>(null);
  const [excluirOpen, setExcluirOpen] = useState(false);
  const [recalcularOpen, setRecalcularOpen] = useState(false);
  const [ordenarOpen, setOrdenarOpen] = useState(false);
  // TEMPORÁRIO: importação da planilha da Flash (remover em dez/2026 ou jan/2027).
  const [importarFlashOpen, setImportarFlashOpen] = useState(false);
  // Ordem das colunas de benefícios escolhida por este usuário (salva no servidor).
  const [ordemColunas, setOrdemColunas] = useState<string[]>([]);
  const [cabecalhoOpen, setCabecalhoOpen] = useState(false);
  const [competenciaEdit, setCompetenciaEdit] = useState("");
  const [dataEdit, setDataEdit] = useState("");
  const [descricaoEdit, setDescricaoEdit] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [erp, setErp] = useState<LancamentosErp | null>(null);
  const [erpOpen, setErpOpen] = useState(false);
  const [vencimento, setVencimento] = useState("");
  const [enviandoErp, setEnviandoErp] = useState(false);

  useEffect(() => {
    api
      .get<BeneficioCatalogo[]>("/beneficios/catalogo")
      .then(setCatalogo)
      .catch(() => toast.error("Erro ao carregar o catálogo de benefícios"));
    api
      .get<User[]>("/colaboradores")
      .then((lista) => setUsuarios(lista.filter((u) => u.ativo !== false)))
      .catch(() => toast.error("Erro ao carregar colaboradores"));
    api
      .get<{ ordem: string[] }>("/beneficios/preferencias/colunas")
      .then((r) => setOrdemColunas(r.ordem))
      .catch(() => setOrdemColunas([]));
  }, []);

  const carregarErp = useCallback(() => {
    api
      .get<LancamentosErp>(`/beneficios/folhas/${id}/erp`)
      .then(setErp)
      .catch(() => setErp(null));
  }, [id]);

  useEffect(() => {
    carregarErp();
  }, [carregarErp]);

  const abrirErp = () => {
    // Sugere o último vencimento usado, senão a data da folha.
    setVencimento(erp?.lancamentos.find((l) => l.dataVencimento)?.dataVencimento ?? folha?.data ?? "");
    setErpOpen(true);
  };

  const enviarErp = async () => {
    setEnviandoErp(true);
    try {
      const r = await api.post<LancamentosErp>(`/beneficios/folhas/${id}/erp/enviar`, {
        dataVencimento: vencimento,
      });
      setErp(r);
      setErpOpen(false);
      const comProblema = r.lancamentos.filter((l) => l.status === "erro" || l.status === "divergente").length;
      if (comProblema > 0) {
        toast.warning(`${comProblema} empresa(s) não foram lançadas corretamente`, {
          description: "Veja o motivo no quadro do ERP.",
        });
      } else {
        toast.success(`${r.resumo.total} conta(s) a pagar lançada(s) no ERP`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao lançar no ERP");
    } finally {
      setEnviandoErp(false);
    }
  };

  // Catálogo na ordem do usuário; o que não está na ordem salva (benefício novo)
  // vem no fim, na ordem do catálogo.
  const catalogoOrdenado = useMemo(() => {
    const pos = new Map(ordemColunas.map((t, i) => [t, i]));
    return catalogo
      .map((b, i) => ({ b, i }))
      .sort((x, y) => (pos.get(x.b.tipo) ?? ordemColunas.length + x.i) - (pos.get(y.b.tipo) ?? ordemColunas.length + y.i))
      .map(({ b }) => b);
  }, [catalogo, ordemColunas]);

  // Colunas: todo benefício lançado na folha (não só nesta página), na ordem
  // escolhida pelo usuário na engrenagem.
  const totaisPorBeneficio = useMemo(() => {
    const totais = resumo?.totaisPorTipo ?? {};
    return catalogoOrdenado.filter((b) => b.tipo in totais).map((b) => ({ ...b, total: totais[b.tipo] }));
  }, [resumo, catalogoOrdenado]);

  // No modal: benefícios ativos e os excluídos que ainda aparecem nesta folha.
  const colunasOrdenaveis: ColunaOrdenavel[] = catalogoOrdenado
    .filter((b) => b.ativo || b.tipo in (resumo?.totaisPorTipo ?? {}))
    .map((b) => ({
      tipo: b.tipo,
      nome: b.nome,
      apoio: !b.ativo ? "excluído" : b.tipo in (resumo?.totaisPorTipo ?? {}) ? undefined : "não está nesta folha",
    }));

  const salvarOrdem = async (ordem: string[]) => {
    try {
      const r = await api.put<{ ordem: string[] }>("/beneficios/preferencias/colunas", { ordem });
      setOrdemColunas(r.ordem);
      toast.success("Ordem das colunas salva");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar a ordem das colunas");
      throw err;
    }
  };

  const pendencias = resumo?.pendenciasCadastro ?? { total: 0, colaboradores: [] };
  const totalColaboradores = resumo?.totalColaboradores ?? 0;
  // O rodapé soma a folha inteira; quando a tabela mostra só parte dela, diz isso.
  const tabelaParcial = totalLinhas !== totalColaboradores || totalLinhas > pageSize;

  const abrirCabecalho = () => {
    if (!folha) return;
    setCompetenciaEdit(folha.competencia);
    setDataEdit(folha.data);
    setDescricaoEdit(folha.descricao ?? "");
    setCabecalhoOpen(true);
  };

  const salvarCabecalho = async () => {
    setSalvando(true);
    try {
      await api.patch(`/beneficios/folhas/${id}`, {
        competencia: competenciaEdit,
        data: dataEdit,
        descricao: descricaoEdit.trim() || null,
      });
      toast.success("Folha atualizada");
      setCabecalhoOpen(false);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSalvando(false);
    }
  };

  const remover = async () => {
    if (!removendo) return;
    setSalvando(true);
    try {
      await api.delete(`/beneficios/folhas/${id}/colaboradores/${removendo.colaborador.id}`);
      toast.success(`${removendo.colaborador.name} removido da folha`);
      setRemovendo(null);
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover");
    } finally {
      setSalvando(false);
    }
  };

  const excluirFolha = async () => {
    setSalvando(true);
    try {
      await api.delete(`/beneficios/folhas/${id}`);
      toast.success("Folha de benefícios excluída");
      nav("/rh/beneficios");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao excluir");
      setSalvando(false);
    }
  };

  const kpis: Kpi[] = resumo
    ? [
        {
          rotulo: "Total da folha",
          valor: formatCents(resumo.total),
          apoio: `${totalColaboradores} colaborador${totalColaboradores === 1 ? "" : "es"}`,
          tom: "destaque",
        },
        ...totaisPorBeneficio.map((b) => ({ rotulo: b.nome, valor: formatCents(b.total) })),
      ]
    : [];

  return (
    <RoleGuard role="rh">
      <AppLayout title="Benefícios">
        <div className="mx-auto flex max-w-[1280px] flex-col gap-4 tabular-nums">
          {carregando && !resposta ? (
            <Loader2 className="mx-auto my-16 h-6 w-6 animate-spin text-muted-foreground" />
          ) : !folha || !resumo ? (
            <p className="py-12 text-center text-muted-foreground">Folha de benefícios não encontrada.</p>
          ) : (
            <>
              <DetailHeader
                voltar={{ to: "/rh/beneficios", label: "Folhas de benefícios" }}
                titulo={
                  <span className="flex items-center gap-1">
                    Benefícios de {formatMesRef(folha.competencia)}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={abrirCabecalho}
                      title="Editar competência, pagamento e descrição"
                      aria-label="Editar competência, pagamento e descrição"
                    >
                      <Pencil className="!size-3.5" />
                    </Button>
                  </span>
                }
                status={
                  folha.exportadaEm ? (
                    <PilulaStatus cor="verde">Exportada em {formatDateTime(folha.exportadaEm)}</PilulaStatus>
                  ) : (
                    <PilulaStatus cor="ambar">Não exportada</PilulaStatus>
                  )
                }
                subtitulo={
                  <>
                    {folha.descricao ?? "Sem descrição"} · Pagamento em {formatDataCivil(folha.data)}
                  </>
                }
                acoes={
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive/5 hover:text-destructive"
                      onClick={() => setExcluirOpen(true)}
                    >
                      <Trash2 />
                      Excluir
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setImportarFlashOpen(true)}
                      title="Substituir a folha pela planilha de pedido da Flash"
                    >
                      <Upload />
                      Importar da Flash
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRecalcularOpen(true)}
                      title="Atualizar pela situação atual dos cadastros"
                    >
                      <RefreshCw />
                      Recalcular
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setLancarOpen(true)}>
                      <Plus />
                      Lançar benefício
                    </Button>
                    <Button variant="outline" size="sm" onClick={abrirErp} disabled={totalColaboradores === 0}>
                      <Send />
                      Lançar no ERP
                    </Button>
                    <ExportarMenu
                      folhaId={folha.id}
                      size="sm"
                      empresas={resumo.empresas}
                      disabled={totalColaboradores === 0}
                      onExportado={() => void load()}
                    />
                  </>
                }
              />

              <FaixaKpis itens={kpis} />

              {pendencias.total > 0 && (
                <div className="flex flex-wrap items-start gap-3 rounded-[10px] border border-amber-500/35 bg-amber-500/[0.08] px-4 py-3 text-[13px] text-amber-800 dark:text-amber-300">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <div className="flex min-w-[240px] flex-1 flex-col gap-1">
                    <span>
                      {pendencias.total} colaborador{pendencias.total === 1 ? "" : "es"} sem CPF ou sem tomador
                      (CNPJ) no cadastro. A Flash exige os dois, então a exportação fica bloqueada até corrigir em
                      Usuários ou remover da folha.
                    </span>
                    <span className="text-xs">
                      {pendencias.colaboradores
                        .slice(0, 8)
                        .map((c) => c.name)
                        .join(", ")}
                      {pendencias.total > 8 && ` e mais ${pendencias.total - 8}`}
                    </span>
                  </div>
                  <Link
                    to="/rh/colaboradores"
                    className="self-center whitespace-nowrap font-semibold underline underline-offset-[3px]"
                  >
                    Corrigir cadastros
                  </Link>
                </div>
              )}

              <section className="overflow-hidden rounded-xl border bg-card">
                <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3.5">
                  <div className="relative w-full max-w-[360px]">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Buscar por nome ou CPF"
                      className="pl-9"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-[13px] text-muted-foreground">
                      {totalColaboradores} colaborador{totalColaboradores === 1 ? "" : "es"} ·{" "}
                      <strong className="font-semibold text-foreground">{formatCents(resumo.total)}</strong>
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground"
                      onClick={() => setOrdenarOpen(true)}
                      title="Ordem das colunas"
                      aria-label="Ordem das colunas"
                    >
                      <Settings className="!size-4" />
                    </Button>
                  </div>
                </div>

                {linhas.length === 0 ? (
                  <p className="border-t py-12 text-center text-sm text-muted-foreground">
                    {totalColaboradores === 0
                      ? "Nenhum benefício lançado. Use \"Lançar benefício\" para começar."
                      : "Nenhum colaborador encontrado."}
                  </p>
                ) : (
                  // Uma coluna por benefício usado nesta folha, como na planilha da Flash.
                  // Colaborador e total ficam fixos nas laterais ao rolar para o lado.
                  <div className={cn("overflow-x-auto border-t", carregando && "opacity-60 transition-opacity")}>
                    <table className="w-full min-w-[1000px] border-collapse text-[13px]">
                      <thead>
                        <tr className="bg-muted-soft text-[11px] uppercase tracking-[0.05em] text-muted-foreground">
                          <th className="sticky left-0 z-[2] min-w-[250px] border-b bg-muted-soft px-4 py-2.5 text-left font-semibold">
                            Colaborador
                          </th>
                          {totaisPorBeneficio.map((b) => (
                            <th
                              key={b.tipo}
                              className="min-w-[110px] border-b px-3.5 py-2.5 text-right font-semibold leading-tight"
                            >
                              {b.nome}
                            </th>
                          ))}
                          <th className="sticky right-0 z-[2] min-w-[190px] border-b border-l bg-muted-soft px-4 py-2.5 text-right font-semibold">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((l) => {
                          const semCpf = soDigitos(l.colaborador.cpf).length !== 11;
                          const semCnpj = soDigitos(l.colaborador.empresaCnpj).length !== 14;
                          return (
                            <tr key={l.colaborador.id} className="group">
                              <td className="sticky left-0 z-[1] border-b border-border-soft bg-card px-4 py-2 group-hover:bg-row-hover">
                                <div className="flex items-center gap-1.5">
                                  <span className="truncate font-medium" title={l.colaborador.name}>
                                    {l.colaborador.name}
                                  </span>
                                  <BadgeVinculo vinculo={l.colaborador.vinculo} />
                                  {!l.colaborador.ativo && (
                                    <span className="rounded border px-1.5 py-px text-[10px] text-muted-foreground">
                                      inativo
                                    </span>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {semCpf ? (
                                    <span className="font-semibold text-destructive">sem CPF</span>
                                  ) : (
                                    formatCPF(l.colaborador.cpf)
                                  )}
                                  {" · "}
                                  {semCnpj ? (
                                    <span className="font-semibold text-destructive">sem tomador</span>
                                  ) : (
                                    (l.colaborador.empresaNome ?? "—")
                                  )}
                                </div>
                              </td>
                              {totaisPorBeneficio.map((b) => (
                                <td
                                  key={b.tipo}
                                  className="border-b border-border-soft px-3.5 py-2 text-right group-hover:bg-row-hover"
                                >
                                  {l.valores[b.tipo] ? (
                                    formatCents(l.valores[b.tipo])
                                  ) : (
                                    <span className="text-muted-foreground/50">—</span>
                                  )}
                                </td>
                              ))}
                              <td className="sticky right-0 z-[1] border-b border-l border-border-soft bg-card py-1.5 pl-4 pr-2.5 group-hover:bg-row-hover">
                                <div className="flex items-center justify-end gap-0.5">
                                  <span className="mr-1.5 font-semibold">{formatCents(l.total)}</span>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground"
                                    onClick={() => setEditando(l)}
                                    title="Ajustar benefícios"
                                    aria-label={`Ajustar benefícios de ${l.colaborador.name}`}
                                  >
                                    <Pencil className="!size-3.5" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => setRemovendo(l)}
                                    title="Remover da folha"
                                    aria-label={`Remover ${l.colaborador.name} da folha`}
                                  >
                                    <Trash2 className="!size-3.5" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-muted-soft font-semibold">
                          <td className="sticky left-0 z-[1] border-t-2 bg-muted-soft px-4 py-2.5">
                            Total da folha
                            {tabelaParcial && (
                              <span className="block text-xs font-normal text-muted-foreground">
                                todos os {totalColaboradores} colaboradores, não só os listados
                              </span>
                            )}
                          </td>
                          {totaisPorBeneficio.map((b) => (
                            <td key={b.tipo} className="border-t-2 px-3.5 py-2.5 text-right">
                              {formatCents(b.total)}
                            </td>
                          ))}
                          <td className="sticky right-0 z-[1] border-l border-t-2 bg-muted-soft py-2.5 pl-4 pr-[74px] text-right">
                            {formatCents(resumo.total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}

                {totalLinhas > 0 && (
                  <div className="border-t px-4 py-3">
                    <Paginacao
                      page={page}
                      pageSize={pageSize}
                      total={totalLinhas}
                      onPage={setPage}
                      carregando={carregando}
                      rotulo="colaboradores"
                    />
                  </div>
                )}
              </section>

              {(erp?.lancamentos.length ?? 0) > 0 && (
                <ErpLancamentosPainel
                  titulo="Contas a pagar no ERP"
                  dados={erp}
                  rotuloNome="Empresa (tomador)"
                  textoVazio=""
                />
              )}
            </>
          )}
        </div>
      </AppLayout>

      {folha && (
        <>
          <LancarBeneficioModal
            open={lancarOpen}
            onClose={() => setLancarOpen(false)}
            folhaId={folha.id}
            catalogo={catalogo}
            usuarios={usuarios}
            onSalvo={() => void load()}
          />
          <EditarBeneficiosModal
            linha={editando}
            onClose={() => setEditando(null)}
            folhaId={folha.id}
            catalogo={catalogo}
            onSalvo={() => void load()}
          />
          <ImportarFlashModal
            open={importarFlashOpen}
            onClose={() => setImportarFlashOpen(false)}
            folhaId={folha.id}
            onSalvo={() => void load()}
          />
          <OrdenarColunasModal
            open={ordenarOpen}
            onClose={() => setOrdenarOpen(false)}
            colunas={colunasOrdenaveis}
            padrao={catalogo.map((b) => b.tipo)}
            onSalvar={salvarOrdem}
          />
          <RecalcularBeneficiosModal
            open={recalcularOpen}
            onClose={() => setRecalcularOpen(false)}
            folhaId={folha.id}
            catalogo={catalogo}
            jaEnviada={!!folha.exportadaEm || (erp?.lancamentos.length ?? 0) > 0}
            onSalvo={() => void load()}
          />
        </>
      )}

      <Dialog open={erpOpen} onOpenChange={setErpOpen}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Lançar benefícios no ERP</DialogTitle>
            <DialogDescription>
              Uma conta a pagar por empresa (tomador), com a Flash como fornecedora
              {resumo ? <>, somando <strong className="text-foreground">{formatCents(resumo.total)}</strong></> : null}.
              Reenviar não duplica: quem já foi lançado continua com a conta original.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <div className="space-y-1.5">
              <Label htmlFor="erp-vencimento">Data de vencimento</Label>
              <Input
                id="erp-vencimento"
                type="date"
                value={vencimento}
                onChange={(e) => setVencimento(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setErpOpen(false)} disabled={enviandoErp}>
              Cancelar
            </Button>
            <Button onClick={() => void enviarErp()} disabled={!vencimento || enviandoErp}>
              {enviandoErp && <Loader2 className="animate-spin" />}
              Lançar no ERP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cabecalhoOpen} onOpenChange={setCabecalhoOpen}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle>Editar folha de benefícios</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-competencia">Competência</Label>
                <Input
                  id="edit-competencia"
                  type="month"
                  value={competenciaEdit}
                  onChange={(e) => setCompetenciaEdit(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-data">Data de pagamento</Label>
                <Input id="edit-data" type="date" value={dataEdit} onChange={(e) => setDataEdit(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-descricao">Descrição</Label>
              <Input
                id="edit-descricao"
                value={descricaoEdit}
                onChange={(e) => setDescricaoEdit(e.target.value)}
                maxLength={200}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCabecalhoOpen(false)} disabled={salvando}>
              Cancelar
            </Button>
            <Button onClick={() => void salvarCabecalho()} disabled={!competenciaEdit || !dataEdit || salvando}>
              {salvando && <Loader2 className="animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removendo} onOpenChange={(v) => !v && setRemovendo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover {removendo?.colaborador.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os benefícios dele nesta folha ({formatCents(removendo?.total ?? 0)}) serão apagados,
              inclusive os fixos. As outras folhas não mudam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={salvando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void remover();
              }}
              disabled={salvando}
              className={buttonVariants({ variant: "destructive" })}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={excluirOpen} onOpenChange={setExcluirOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir esta folha de benefícios?</AlertDialogTitle>
            <AlertDialogDescription>
              Todos os lançamentos desta folha serão apagados. Não é possível desfazer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={salvando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void excluirFolha();
              }}
              disabled={salvando}
              className={buttonVariants({ variant: "destructive" })}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </RoleGuard>
  );
};

export default RHBeneficioDetalhe;
